import React, { useEffect, useState } from 'react';
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Button, Tabs, Tab, IconButton, Container } from '@mui/material';
import { FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import axios from 'axios';
import { API_BASE_URL } from '../../App';
import dayjs from 'dayjs';
import ClubhouseBookingModal from './components/ClubhouseBookingModal';
import ConfirmModal from '../../components/ConfirmModal';

const Clubhouse = () => {
    const [tabValue, setTabValue] = useState(0); // 0 = Aktuell, 1 = Archiv
    const [bookings, setBookings] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [showConfirm, setShowConfirm] = useState(false);
    const [deleteId, setDeleteId] = useState(null);

    const fetchBookings = () => {
        axios.get(`${API_BASE_URL}/api/clubhouse`, { withCredentials: true })
            .then(res => {
                setBookings(res.data);
            })
            .catch(err => console.error("Error fetching clubhouse bookings:", err));
    };

    useEffect(() => {
        fetchBookings();
    }, []);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleAdd = () => {
        setSelectedBooking(null);
        setShowModal(true);
    };

    const handleEdit = (booking) => {
        setSelectedBooking(booking);
        setShowModal(true);
    };

    const confirmDelete = (id) => {
        setDeleteId(id);
        setShowConfirm(true);
    };

    const handleDelete = () => {
        if (deleteId) {
            axios.delete(`${API_BASE_URL}/api/clubhouse/${deleteId}`, { withCredentials: true })
                .then(() => {
                    fetchBookings();
                    setShowConfirm(false);
                    setDeleteId(null);
                })
                .catch(err => alert("Fehler beim Löschen: " + err.message));
        }
    };

    const today = dayjs().startOf('day');

    const filteredBookings = bookings.filter(b => {
        const bookingDate = dayjs(b.date);
        if (tabValue === 0) {
            // Include today
            return bookingDate.isSame(today) || bookingDate.isAfter(today);
        } else {
            return bookingDate.isBefore(today);
        }
    }).sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return tabValue === 0 ? dateA - dateB : dateB - dateA; // Current: asc, Archive: desc
    });

    return (
        <Container maxWidth="lg" disableGutters>
            <Paper sx={{ p: { xs: 2, md: 3 }, mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h5" component="h1" fontWeight="bold" color="primary">
                        Clubheim Belegung
                    </Typography>
                    <Button variant="contained" startIcon={<FaPlus />} onClick={handleAdd}>
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Neuer Eintrag</Box>
                        <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Neu</Box>
                    </Button>
                </Box>

                <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
                    <Tab label="Aktuell" />
                    <Tab label="Archiv" />
                </Tabs>

                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Info</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', display: { xs: 'none', md: 'table-cell' } }}>Gemietet von</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', display: { xs: 'none', md: 'table-cell' } }}>Notiz</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', display: { xs: 'none', md: 'table-cell' } }}>Eingetragen von</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Aktionen</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredBookings.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" className="fs-7">Keine Einträge gefunden.</TableCell>
                                </TableRow>
                            ) : (
                                filteredBookings.map((booking) => (
                                    <TableRow key={booking._id} hover>
                                        <TableCell className="fs-7">
                                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'center' }, gap: { xs: 0, md: 1 } }}>
                                                <Box sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{dayjs(booking.date).format('DD.MM.YYYY')}</Box>
                                                {booking.startTime && <Box sx={{ color: 'text.secondary', fontSize: '0.9em', whiteSpace: 'nowrap' }}>ab {booking.startTime} Uhr</Box>}
                                                <Box sx={{ display: { xs: 'block', md: 'none' }, mt: 0.5, color: 'primary.main', fontWeight: 500 }}>
                                                    {booking.rentedBy && booking.rentedBy.firstName ? `${booking.rentedBy.firstName} ${booking.rentedBy.lastName}` : '-'}
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell className="fs-7" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                            {booking.rentedBy && booking.rentedBy.firstName ? `${booking.rentedBy.firstName} ${booking.rentedBy.lastName}` : '-'}
                                        </TableCell>
                                        <TableCell className="fs-7" sx={{ display: { xs: 'none', md: 'table-cell' } }}>{booking.notes}</TableCell>
                                        <TableCell className="fs-7" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                            {booking.createdBy && booking.createdBy.firstName ? `${booking.createdBy.firstName} ${booking.createdBy.lastName}` : '-'}
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton onClick={() => handleEdit(booking)} color="primary" size="small">
                                                <FaEdit />
                                            </IconButton>
                                            <IconButton onClick={() => confirmDelete(booking._id)} color="error" size="small">
                                                <FaTrash />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <ClubhouseBookingModal
                show={showModal}
                onHide={() => setShowModal(false)}
                booking={selectedBooking}
                refresh={fetchBookings}
            />

            <ConfirmModal
                open={showConfirm}
                onClose={() => setShowConfirm(false)}
                onConfirm={handleDelete}
                title="Buchung löschen"
                message="Möchten Sie diesen Eintrag wirklich löschen?"
                confirmText="Löschen"
                type="delete"
            />
        </Container>
    );
};

export default Clubhouse;
